import React, { useEffect, useState } from "react";

interface AnalogClockProps {
  size?: number;
  style?: "minimal" | "dots" | "ticks" | "numeric" | "swiss" | "polygon" | "square" | "duotone" | "bauhaus";
  showSecondsHand?: boolean;
  hourHandColor?: string;
  minuteHandColor?: string;
  secondHandColor?: string;
  smoothSeconds?: boolean;
  hourHandLength?: number;
  minuteHandLength?: number;
  secondHandLength?: number;
  className?: string;
}

const AnalogClock: React.FC<AnalogClockProps> = ({
  size = 280,
  style = "ticks",
  showSecondsHand = true,
  hourHandColor = "#FFA500",
  minuteHandColor = "#007AFF",
  secondHandColor = "#FF3B30",
  smoothSeconds = true,
  hourHandLength = 35,
  minuteHandLength = 50,
  secondHandLength = 45,
  className = "",
}) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(
      () => {
        setTime(new Date());
      },
      smoothSeconds && showSecondsHand ? 50 : 1000
    );

    return () => clearInterval(intervalId);
  }, [smoothSeconds, showSecondsHand]);

  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  const milliseconds = time.getMilliseconds();

  const secondDeg =
    smoothSeconds && showSecondsHand ? ((seconds + milliseconds / 1000) / 60) * 360 : (seconds / 60) * 360;
  const minuteDeg = ((minutes + seconds / 60) / 60) * 360;
  const hourDeg = (((hours % 12) + minutes / 60) / 12) * 360;

  const clockRadius = size / 2;
  const borderWidth = Math.max(1, Math.floor(size * 0.005));
  const hourHandWidth = Math.max(2, Math.floor(size * 0.015));
  const minuteHandWidth = Math.max(1.5, Math.floor(size * 0.01));
  const secondHandWidth = Math.max(1, Math.floor(size * 0.005));
  const centerDotSize = Math.max(4, Math.floor(size * 0.03));

  const actualHourHandLength = (clockRadius * hourHandLength) / 100;
  const actualMinuteHandLength = (clockRadius * minuteHandLength) / 100;
  const actualSecondHandLength = (clockRadius * secondHandLength) / 100;

  // Generate ticks for minute and hour markers
  const minuteTicks = Array.from({ length: 60 }, (_, i) => ({
    angle: i * 6,
    isHour: i % 5 === 0,
    x: clockRadius + (clockRadius - 10) * Math.sin((i * 6 * Math.PI) / 180),
    y: clockRadius - (clockRadius - 10) * Math.cos((i * 6 * Math.PI) / 180),
    length: i % 5 === 0 ? clockRadius * 0.08 : clockRadius * 0.04,
  }));

  const hourTicks = Array.from({ length: 12 }, (_, i) => ({
    angle: i * 30,
    hour: i === 0 ? 12 : i,
    x: clockRadius + clockRadius * 0.92 * Math.sin((i * 30 * Math.PI) / 180),
    y: clockRadius - clockRadius * 0.92 * Math.cos((i * 30 * Math.PI) / 180),
  }));

  // Polygon points for polygon style
  const getPolygonPoints = (sides: number, radius: number, rotation = 0) => {
    const points = [];
    const angleStep = 360 / sides;
    for (let i = 0; i < sides; i++) {
      const angle = ((i * angleStep + rotation) * Math.PI) / 180;
      points.push({
        x: clockRadius + radius * Math.cos(angle),
        y: clockRadius + radius * Math.sin(angle),
      });
    }
    return points;
  };

  const polygonRadius = clockRadius - borderWidth;
  const polygonPoints = getPolygonPoints(size >= 300 ? 12 : 8, polygonRadius, 0);
  const polygonPointsStr = polygonPoints.map((p) => `${p.x},${p.y}`).join(" ");

  const innerCircleSize = size * 0.85;

  return (
    <div
      className={`relative ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      {/* Clock Face */}
      {style === "minimal" && (
        <div
          className="absolute inset-0 rounded-full"
          style={
            {
              // border: `${borderWidth}px solid var(--clock-border-color, #6a6a6a)`,
            }
          }
        />
      )}

      {style === "ticks" && (
        <>
          {/* <div
            className="absolute inset-0 rounded-full"
            style={{
              border: `${borderWidth}px solid var(--clock-border-color, #6a6a6a)`,
            }}
          /> */}
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute top-0 left-0">
            {minuteTicks.map((tick, idx) => (
              <line
                key={`tick-${idx}`}
                x1={tick.x}
                y1={tick.y}
                x2={tick.x - tick.length * Math.sin((tick.angle * Math.PI) / 180)}
                y2={tick.y + tick.length * Math.cos((tick.angle * Math.PI) / 180)}
                stroke={tick.isHour ? "var(--tick-hour-color, #b0b0b0)" : "var(--tick-minute-color, #7a7a7a)"}
                strokeWidth={tick.isHour ? borderWidth : borderWidth / 2}
              />
            ))}
          </svg>
        </>
      )}

      {style === "dots" && (
        <>
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: `${borderWidth}px solid var(--clock-border-color, #6a6a6a)`,
            }}
          />
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute top-0 left-0">
            {hourTicks.map((tick, idx) => (
              <circle
                key={`dot-${idx}`}
                cx={tick.x}
                cy={tick.y}
                r={size * 0.02}
                fill="var(--dot-hour-color, #9a9a9a)"
              />
            ))}
          </svg>
        </>
      )}

      {style === "numeric" && (
        <>
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: `${borderWidth}px solid var(--clock-border-color, #6a6a6a)`,
            }}
          />
          <div className="absolute inset-0">
            {hourTicks.map((tick, idx) => (
              <div
                key={`num-${idx}`}
                className="absolute"
                style={{
                  fontSize: `${size * 0.11}px`,
                  color: "var(--tick-hour-color, #b0b0b0)",
                  left: `${tick.x}px`,
                  top: `${tick.y}px`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                {tick.hour}
              </div>
            ))}
          </div>
        </>
      )}

      {style === "swiss" && (
        <>
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: `${borderWidth * 2}px solid var(--clock-border-color, #6a6a6a)`,
            }}
          />
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute top-0 left-0">
            {hourTicks.map((tick, idx) => (
              <line
                key={`swiss-${idx}`}
                x1={tick.x}
                y1={tick.y}
                x2={tick.x - clockRadius * 0.15 * Math.sin((tick.angle * Math.PI) / 180)}
                y2={tick.y + clockRadius * 0.15 * Math.cos((tick.angle * Math.PI) / 180)}
                stroke="var(--tick-hour-color, #b0b0b0)"
                strokeWidth={borderWidth * 2}
                strokeLinecap="round"
              />
            ))}
          </svg>
        </>
      )}

      {style === "polygon" && (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute top-0 left-0">
          <polygon
            points={polygonPointsStr}
            fill="none"
            stroke="var(--clock-border-color, #6a6a6a)"
            strokeWidth={borderWidth}
          />
        </svg>
      )}

      {style === "square" && (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute top-0 left-0">
          <rect
            x={clockRadius * 0.2}
            y={clockRadius * 0.2}
            width={size - clockRadius * 0.4}
            height={size - clockRadius * 0.4}
            fill="none"
            stroke="var(--clock-border-color, #6a6a6a)"
            strokeWidth={borderWidth}
          />
          <defs>
            <clipPath id="square-clip">
              <rect
                x={clockRadius * 0.2 + borderWidth}
                y={clockRadius * 0.2 + borderWidth}
                width={size - clockRadius * 0.4 - borderWidth * 2}
                height={size - clockRadius * 0.4 - borderWidth * 2}
              />
            </clipPath>
          </defs>
        </svg>
      )}

      {style === "duotone" && (
        <>
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: `${borderWidth}px solid var(--clock-border-color, #6a6a6a)`,
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: `${innerCircleSize}px`,
              height: `${innerCircleSize}px`,
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "var(--bg-secondary, #3a3a3a)",
            }}
          />
        </>
      )}

      {style === "bauhaus" && (
        <div className="absolute inset-0 rounded-full overflow-hidden">
          <div
            className="absolute inset-0 rounded-full"
            // style={{
            //   border: `${borderWidth}px solid var(--clock-border-color, #6a6a6a)`,
            // }}
          />
          <div
            className="absolute left-0 top-0 w-1/2 h-1/2"
            style={{ backgroundColor: "var(--accent-color, #3b82f6)", opacity: 0.2 }}
          />
          <div
            className="absolute right-0 bottom-0 w-1/2 h-1/2"
            style={{ backgroundColor: "var(--dot-hour-color, #b48ead)", opacity: 0.2 }}
          />
          {/* <div
            className="absolute left-1/2 top-0"
            style={{
              width: "1px",
              height: `${clockRadius / 4}px`,
              backgroundColor: "var(--accent-color, #3b82f6)",
              transform: "translateX(-50%)",
            }}
          />
          <div
            className="absolute right-0 top-1/2"
            style={{
              height: "1px",
              width: `${clockRadius / 4}px`,
              backgroundColor: "var(--dot-hour-color, #b48ead)",
              transform: "translateY(-50%)",
            }}
          />
          <div
            className="absolute left-1/2 bottom-0"
            style={{
              width: "1px",
              height: `${clockRadius / 4}px`,
              backgroundColor: "var(--accent-color, #3b82f6)",
              transform: "translateX(-50%)",
            }}
          />
          <div
            className="absolute left-0 top-1/2"
            style={{
              height: "1px",
              width: `${clockRadius / 4}px`,
              backgroundColor: "var(--dot-hour-color, #b48ead)",
              transform: "translateY(-50%)",
            }}
          /> */}
        </div>
      )}

      {/* Clock Hands */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute top-0 left-0 z-10"
        style={style === "square" ? { clipPath: "url(#square-clip)" } : {}}
      >
        {/* Hour Hand */}
        <line
          x1={clockRadius}
          y1={clockRadius}
          x2={clockRadius + actualHourHandLength * Math.sin((hourDeg * Math.PI) / 180)}
          y2={clockRadius - actualHourHandLength * Math.cos((hourDeg * Math.PI) / 180)}
          stroke={hourHandColor}
          strokeWidth={hourHandWidth}
          strokeLinecap="round"
        />

        {/* Minute Hand */}
        <line
          x1={clockRadius}
          y1={clockRadius}
          x2={clockRadius + actualMinuteHandLength * Math.sin((minuteDeg * Math.PI) / 180)}
          y2={clockRadius - actualMinuteHandLength * Math.cos((minuteDeg * Math.PI) / 180)}
          stroke={minuteHandColor}
          strokeWidth={minuteHandWidth}
          strokeLinecap="round"
        />

        {/* Second Hand */}
        {showSecondsHand && (
          <>
            <line
              x1={clockRadius}
              y1={clockRadius}
              x2={clockRadius + actualSecondHandLength * Math.sin((secondDeg * Math.PI) / 180)}
              y2={clockRadius - actualSecondHandLength * Math.cos((secondDeg * Math.PI) / 180)}
              stroke={secondHandColor}
              strokeWidth={secondHandWidth}
              strokeLinecap="round"
              style={smoothSeconds ? { transition: "all 50ms linear" } : {}}
            />

            <circle
              cx={clockRadius - secondHandWidth * 2 * Math.sin((secondDeg * Math.PI) / 180)}
              cy={clockRadius + secondHandWidth * 2 * Math.cos((secondDeg * Math.PI) / 180)}
              r={secondHandWidth * 1.5}
              fill={secondHandColor}
              style={smoothSeconds ? { transition: "all 50ms linear" } : {}}
            />
          </>
        )}

        {/* Center Dot */}
        <circle cx={clockRadius} cy={clockRadius} r={centerDotSize / 2} fill="var(--center-dot-color, #b0b0b0)" />
      </svg>
    </div>
  );
};

export default AnalogClock;
